import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { Link, useNavigate } from 'react-router-dom'
import { useParams } from 'react-router-dom'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { useState as useDropdownState } from 'react'

const PAGE_SIZE = 50

function NActTest() {
  const [testRecords, setTestRecords] = useState([])
  const [sections, setSections] = useState([])
  const [selectedSection, setSelectedSection] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const { id } = useParams()
  const dropdownRef = useRef(null)
  const [dropdownOpen, setDropdownOpen] = useDropdownState(false)

  useEffect(() => {
    if (id) getTestRecords()
    // eslint-disable-next-line
  }, [id])

  const getTestRecords = async () => {
    setIsLoading(true)
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_URL}getActtest?test_id=${id}`,
      {
        headers: {
          token:
            typeof window !== 'undefined' ? localStorage.getItem('token') : '',
        },
      }
    )
    if (response.status === 200) {
      setTestRecords(response.data.data)
      // Extract unique sections
      const uniqueSections = [
        ...new Set(response.data.data.map((item) => item.section)),
      ]
      setSections(uniqueSections)
      setSelectedSection(uniqueSections[0] || '')
    }
    setIsLoading(false)
  }

  // Filter records for selected section
  const sectionRecords = testRecords.filter(
    (rec) => rec.section === selectedSection
  )
  const totalPages = Math.ceil(sectionRecords.length / PAGE_SIZE) || 1
  const paginatedRecords = sectionRecords.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page)
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [selectedSection])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <section className="bg-white border rounded-[10px] border-[#F0F0F0] w-full px-0 md:px-4 mt-4 shadow-sm">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b px-4 py-4 border-[#F0F0F0] gap-4">
        <h2 className="text-xl text-[#030303] font-bold">Act Test Key</h2>
        <div className="flex items-center gap-3">
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className="flex items-center gap-2 px-4 py-2 bg-white border border-[#F0F0F0] rounded-md shadow-sm text-[#535862] font-medium hover:border-[#DE3C7D] focus:border-[#DE3C7D] focus:outline-none transition"
              onClick={() => setDropdownOpen((open) => !open)}
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
            >
              {selectedSection || 'Select Section'}
              <ChevronDownIcon
                className={`w-4 h-4 transition-transform ${
                  dropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {dropdownOpen && (
              <ul
                className="absolute left-0 mt-2 min-w-full bg-white border border-[#F0F0F0] rounded-md shadow-lg z-10 p-0"
                tabIndex={-1}
                role="listbox"
              >
                {sections.map((section, idx) => (
                  <li
                    key={section}
                    className={`w-full cursor-pointer hover:bg-[#F7EAF2] ${
                      selectedSection === section
                        ? 'bg-[#DE3C7D] text-white hover:bg-[#DE3C7D]'
                        : 'text-[#535862]'
                    } p-0 m-0`}
                    onClick={() => {
                      setSelectedSection(section)
                      setDropdownOpen(false)
                    }}
                    role="option"
                    aria-selected={selectedSection === section}
                  >
                    <div
                      className={`w-full py-2 text-center ${
                        idx !== sections.length - 1
                          ? 'border-b border-[#F0F0F0]'
                          : ''
                      }`}
                    >
                      {section}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Link
            to="/tests"
            className="flex items-center gap-2 text-white text-center bg-[#DE3C7D] p-2.5 rounded-md hover:bg-[#c8356f] transition-colors"
            title="Back to Tests"
          >
            <i className="fa fa-arrow-left"></i>
            <span>Back to Tests</span>
          </Link>
        </div>
      </div>
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#DE3C7D]"></div>
          </div>
        ) : sectionRecords.length === 0 ? (
          <div className="text-center py-8 text-[#535862]">
            No questions found for this section.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className="text-sm text-[#535862] font-medium text-center border w-[100px] px-5 py-3.5 border-[#F0F0F0]">
                  Question
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-60 px-5 py-3.5 border-[#F0F0F0]">
                  Topic
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-60 px-5 py-3.5 border-[#F0F0F0]">
                  Sub Topic
                </th>
                <th className="text-sm text-[#535862] font-medium text-center border w-40 px-5 py-3.5 border-[#F0F0F0]">
                  Correct Answer
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedRecords.map((record, idx) => (
                <tr key={record._id || idx} className="border border-[#F0F0F0]">
                  <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                    {record.question_number}
                  </td>
                  <td className="text-left border px-5 py-2 border-[#F0F0F0]">
                    {record.topic}
                  </td>
                  <td className="text-left border px-5 py-2 border-[#F0F0F0]">
                    {record.subtopic}
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                    {record.answer}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <div className="flex justify-end items-center gap-2 px-5 py-4">
          <button
            className={`px-3 py-1 rounded-md border text-sm font-medium ${
              currentPage === 1
                ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                : 'text-[#DE3C7D] border-[#DE3C7D] hover:bg-[#FFF0F6]'
            }`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {[...Array(totalPages)].map((_, idx) => (
            <button
              key={idx}
              className={`px-3 py-1 rounded-md border text-sm font-medium ${
                currentPage === idx + 1
                  ? 'bg-[#DE3C7D] text-white border-[#DE3C7D]'
                  : 'text-[#DE3C7D] border-[#DE3C7D] hover:bg-[#FFF0F6]'
              }`}
              onClick={() => handlePageChange(idx + 1)}
            >
              {idx + 1}
            </button>
          ))}
          <button
            className={`px-3 py-1 rounded-md border text-sm font-medium ${
              currentPage === totalPages
                ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                : 'text-[#DE3C7D] border-[#DE3C7D] hover:bg-[#FFF0F6]'
            }`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </section>
  )
}

export default NActTest
